import { useState, useCallback } from 'react';

/**
 * Hook para manejar notificaciones
 * Compatible con el sistema de validaciones del backend
 */

let notificationId = 0;

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((message) => {
    const id = notificationId++;
    const notification = {
      id,
      ...message,
      timestamp: message.timestamp || new Date().toISOString()
    };
    
    setNotifications((prev) => [...prev, notification]);
    
    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Métodos de conveniencia
  const success = useCallback((title, message, details = null) => {
    return addNotification({
      type: 'success',
      severity: 'low',
      title,
      message,
      details
    });
  }, [addNotification]);

  const error = useCallback((title, message, field = null, details = null, severity = 'high') => {
    return addNotification({
      type: 'error',
      severity,
      title,
      message,
      field,
      details
    });
  }, [addNotification]);

  const warning = useCallback((title, message, details = null) => {
    return addNotification({
      type: 'warning',
      severity: 'medium',
      title,
      message,
      details
    });
  }, [addNotification]);

  const info = useCallback((title, message, details = null) => {
    return addNotification({
      type: 'info',
      severity: 'low',
      title,
      message,
      details
    });
  }, [addNotification]);

  // Método para procesar mensajes del backend
  const addBackendMessage = useCallback((backendMessage) => {
    return addNotification(backendMessage);
  }, [addNotification]);

  // Método para procesar array de mensajes del backend
  const addBackendMessages = useCallback((backendMessages) => {
    if (Array.isArray(backendMessages)) {
      backendMessages.forEach(msg => addBackendMessage(msg));
    }
  }, [addBackendMessage]);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    success,
    error,
    warning,
    info,
    addBackendMessage,
    addBackendMessages
  };
};

export default useNotifications;
